export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeoutMs?: number;
  private readonly fetcher: typeof fetch;

  constructor(options: {
    baseUrl: string;
    apiKey: string;
    timeoutMs?: number;
    fetcher?: typeof fetch;
  }) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.apiKey = options.apiKey;
    this.timeoutMs = options.timeoutMs;
    this.fetcher = options.fetcher ?? fetch;
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const controller = this.timeoutMs ? new AbortController() : null;
    const timeoutId = this.timeoutMs
      ? setTimeout(() => controller?.abort(), this.timeoutMs)
      : null;

    try {
      const fetcher = this.fetcher.bind(globalThis);
      const response = await fetcher(this.baseUrl + path, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + this.apiKey,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller?.signal,
      });

      const text = await response.text();
      const contentType = response.headers.get("Content-Type") ?? "";
      const isJson = contentType.toLowerCase().includes("application/json");
      const data = text && isJson ? JSON.parse(text) : (text || null);

      if (!response.ok) {
        throw new ApiError("API request failed", response.status, data);
      }

      return data as T;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }
}
